import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  History, 
  Filter,
  Plus,
  Pencil,
  Trash2,
  Home,
  User,
  CreditCard,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const entityTypeLabels = {
  room: 'Kamar',
  tenant: 'Penghuni',
  payment: 'Pembayaran',
  expense: 'Biaya Operasional'
};

const entityTypeIcons = {
  room: Home,
  tenant: User,
  payment: CreditCard,
  expense: Wallet
};

const actionLabels = {
  create: 'Dibuat',
  update: 'Diubah',
  delete: 'Dihapus'
};

const actionColors = {
  create: 'bg-green-100 text-green-700 border-green-200',
  update: 'bg-blue-100 text-blue-700 border-blue-200',
  delete: 'bg-red-100 text-red-700 border-red-200'
};

const getActionIcon = (action) => {
  switch (action) {
    case 'create': return <Plus className="w-3 h-3" />;
    case 'update': return <Pencil className="w-3 h-3" />;
    case 'delete': return <Trash2 className="w-3 h-3" />;
    default: return null;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatChanges = (changes, action) => {
  if (action === 'create' || action === 'delete') {
    const data = changes.created || changes.deleted;
    if (!data) return null;
    return (
      <div className="text-xs text-muted-foreground space-y-0.5">
        {Object.entries(data).slice(0, 3).map(([key, value]) => (
          <div key={key} className="truncate">
            <span className="font-medium">{key}:</span>{' '}
            <span className="break-all">{typeof value === 'object' ? JSON.stringify(value) : String(value).substring(0, 50)}</span>
          </div>
        ))}
        {Object.keys(data).length > 3 && (
          <div className="text-muted-foreground">...dan {Object.keys(data).length - 3} lainnya</div>
        )}
      </div>
    );
  }
  
  // For update action
  return (
    <div className="space-y-1">
      {Object.entries(changes).slice(0, 3).map(([field, change]) => (
        <div key={field} className="text-xs">
          <span className="font-medium text-muted-foreground">{field}:</span>
          <div className="flex flex-wrap items-center gap-1 mt-0.5">
            <span className="text-red-600 line-through break-all">{String(change.old).substring(0, 30)}</span>
            <span>→</span>
            <span className="text-green-600 break-all">{String(change.new).substring(0, 30)}</span>
          </div>
        </div>
      ))}
      {Object.keys(changes).length > 3 && (
        <div className="text-xs text-muted-foreground">...dan {Object.keys(changes).length - 3} perubahan lainnya</div>
      )}
    </div>
  );
};

// Mobile Card Component for Audit Log
const LogCard = ({ log }) => {
  const EntityIcon = entityTypeIcons[log.entity_type] || History;
  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        {/* Header: Entity + Action Badge */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
              <EntityIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{log.entity_name}</p>
              <p className="text-xs text-muted-foreground">
                {entityTypeLabels[log.entity_type] || log.entity_type}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={`${actionColors[log.action]} flex-shrink-0`}>
            {getActionIcon(log.action)}
            <span className="ml-1">{actionLabels[log.action]}</span>
          </Badge>
        </div>

        {/* Changes */}
        <div className="bg-muted/50 rounded-lg p-3 mb-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Perubahan:</p>
          {formatChanges(log.changes, log.action)}
        </div>

        {/* Footer: Time + User */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span>{formatDate(log.created_at)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-3 h-3 text-primary" />
            </div>
            <span>{log.user_name}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('entity_type', filterType);
      params.append('limit', limit.toString());
      params.append('skip', (page * limit).toString());

      const response = await axios.get(`${API_URL}/api/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Gagal memuat riwayat perubahan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, page]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <History className="w-6 h-6" />
            Riwayat Perubahan
          </h1>
          <p className="text-muted-foreground text-sm">Audit log semua perubahan data</p>
        </div>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(0); }}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-entity-type">
                  <SelectValue placeholder="Filter jenis data" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="room">Kamar</SelectItem>
                  <SelectItem value="tenant">Penghuni</SelectItem>
                  <SelectItem value="payment">Pembayaran</SelectItem>
                  <SelectItem value="expense">Biaya Operasional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-muted-foreground">
              Total: {total} perubahan
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Belum ada riwayat perubahan</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile View - Cards */}
          <div className="md:hidden space-y-3">
            {logs.map((log) => (
              <LogCard key={log.id} log={log} />
            ))}
          </div>

          {/* Desktop View - Table */}
          <Card className="hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Waktu</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="w-[100px]">Aksi</TableHead>
                  <TableHead>Perubahan</TableHead>
                  <TableHead className="w-[150px]">Oleh</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const EntityIcon = entityTypeIcons[log.entity_type] || History;
                  return (
                    <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                            <EntityIcon className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{log.entity_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {entityTypeLabels[log.entity_type] || log.entity_type}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={actionColors[log.action]}>
                          {getActionIcon(log.action)}
                          <span className="ml-1">{actionLabels[log.action]}</span>
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {formatChanges(log.changes, log.action)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-3 h-3 text-primary" />
                          </div>
                          <span className="text-sm">{log.user_name}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <span className="text-sm text-muted-foreground">
                Halaman {page + 1} dari {totalPages}
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="rounded-xl"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-xl"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
